import { PredefinedCommentsListContext } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-list/contexts/predefined-comments-list.context';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { getPredefinedCommentsList } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/service/predefined-comments.service';
import { PredefinedComment } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/predefined-comments.model';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';

export const PredefinedCommentsListProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true);
    const [selectedRows, setSelectedRows] = useState<PredefinedComment[]>([]);

    const { data: comments = [], isFetching: isTableLoading } = usePocQuery<PredefinedComment[]>({
        queryKey: settingsKeys.predefinedCommentsList(showOnlyActive),
        queryFn: () => executeRequest(() => getPredefinedCommentsList(showOnlyActive)),
    });

    // Restores the legacy reload contract: clear the (now-stale) selection, then invalidate the list
    // query. Called after an activate/deactivate mutation — without the reset, the acted-on rows linger
    // in the bulk-action bar with a stale `active` flag (offering an action for rows no longer visible,
    // or the wrong action when showOnlyActive is false).
    const refreshList = useCallback(() => {
        setSelectedRows([]);
        return updateQuery(settingsKeys.predefinedComments());
    }, []);

    const handleOnSelectRow = useCallback(
        (id: string, isSelected: boolean) => {
            const comment = comments.find((c) => c.id === id);
            if (!comment) {
                return;
            }

            setSelectedRows((prev) => (isSelected ? [...prev, comment] : prev.filter((row) => row.id !== id)));
        },
        [comments]
    );

    const handleShowOnlyActiveChange = useCallback((value: boolean) => {
        setShowOnlyActive(value);
        setSelectedRows([]);
    }, []);

    const value = useMemo(
        () => ({
            comments,
            isTableLoading,
            showOnlyActive,
            setShowOnlyActive,
            refreshList,
            selectedRows,
            setSelectedRows,
            handleOnSelectRow,
            handleShowOnlyActiveChange,
        }),
        [
            comments,
            handleOnSelectRow,
            isTableLoading,
            selectedRows,
            setSelectedRows,
            handleShowOnlyActiveChange,
            showOnlyActive,
            refreshList,
        ]
    );
    return <PredefinedCommentsListContext.Provider value={value}>{children}</PredefinedCommentsListContext.Provider>;
};
